import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { LazyChatMarkdown } from '../../components/ChatMarkdown/LazyChatMarkdown'

import {
  useCancelCustomAgentRunMutation,
  useGetAuthzMeQuery,
  useListProblemOccurrencesQuery,
  useWorkScope,
  useDispatchCustomAgentRunMutation,
  useGetCustomAgentConfigQuery,
  useGetCustomAgentRunQuery,
  useListCustomAgentResultItemsQuery,
  useListCustomAgentResultsQuery,
  usePatchCustomAgentResultFeedbackMutation
} from '@axilon-apps/domains-ontology-ui-state'

import { WorkError } from '../../components/problem-work/OccurrenceTasks'
import { ProblemSummaryRow } from '../../components/problem-work/ProblemSummaryRow'
import { DraftProblems } from '../../components/problem-work/DraftProblems'
import { SchemaAwareResultView } from '../../components/custom-agents/SchemaAwareResultView'
import { Empty } from '../../components/primitives/Empty'
import {
  PmPresenter,
  RcaPresenter,
  SummaryPresenter,
  pickPresenter,
  presenterHeadline
} from '../../components/result-presenters'
import { isGlobalAdmin } from '../../lib/global-admin'
import { occurrenceHref, runPageHref } from '../../lib/component-links'
import { HistoricalVersionBanner } from './FollowUpBanners'
import { FollowUpVersionPicker } from './FollowUpVersionPicker'
import { RunDetailDisclosure } from './RunDetailDisclosure'
import { RunFailure } from './RunFailure'
import { RunPageHeader } from './RunPageHeader'
import { RunProgress, RunQueued } from './RunProgress'
import { RunSection } from './RunSection'
import { isLiveRunState } from './run-status'
import { revisionQueryArg } from './useInsightRunRevision'
import { useNow } from './useNow'

interface Props {
  runId: string
}

/**
 * Per-run view for a CUSTOM AGENT run: header, status, and the emitted
 * result. Canonical investigations keep their own run identity; legacy
 * follow-ups redirect to their parent.
 */
export function CustomAgentRunView({ runId }: Props) {
  const navigate = useNavigate()
  const { data: authz, isError: authzError } = useGetAuthzMeQuery()
  const permissions = authzError ? [] : (authz?.permissions ?? [])
  const canReadConfig = permissions.includes('agents:custom:config/read')
  const canConfigure = !authzError && isGlobalAdmin(authz?.roles) && canReadConfig
  const canCancel = permissions.includes('agents:custom:run/cancel')
  const canDispatch = permissions.includes('agents:custom:run/dispatch')
  const canReview = permissions.includes('insight/triage')
  const [rerunError, setRerunError] = useState<string | null>(null)
  const [viewRevision, setViewRevision] = useState<number | null>(null)

  const runResult = useGetCustomAgentRunQuery(runId)
  const run = runResult.data
  const runState = run?.state
  const now = useNow(isLiveRunState(runState ?? ''))

  const [cancelRun, { isLoading: cancelling }] = useCancelCustomAgentRunMutation()
  const [dispatchRun, { isLoading: rerunning }] = useDispatchCustomAgentRunMutation()

  const configResult = useGetCustomAgentConfigQuery(run?.custom_agent_config_id ?? '', {
    skip: !canReadConfig || !run?.custom_agent_config_id
  })
  const config = canReadConfig && !configResult.isError ? configResult.data : undefined

  const knownCurrent =
    run?.current_revision && run.current_revision > 0 ? run.current_revision : undefined
  const currentRevision = knownCurrent ?? 1
  const selectedRevision = viewRevision ?? currentRevision
  const viewingHistory = Boolean(knownCurrent && selectedRevision < knownCurrent)
  const resultsRevision = revisionQueryArg(viewRevision, knownCurrent)

  const resultsQuery = useListCustomAgentResultsQuery(
    {
      configId: run?.custom_agent_config_id ?? '',
      runId,
      pageSize: 1,
      revision: resultsRevision
    },
    {
      skip: !canReadConfig || !run?.custom_agent_config_id || run.state !== 'completed'
    }
  )
  const resultDocument =
    canReadConfig && !resultsQuery.isError ? resultsQuery.currentData?.results?.[0] : undefined
  const result = resultDocument?.result ?? null
  const hasResult = !!result && Object.keys(result).length > 0
  const eventItemsQuery = useListCustomAgentResultItemsQuery(
    {
      configId: run?.custom_agent_config_id ?? '',
      pageSize: 100,
      runId,
      revision: resultsRevision,
      type: 'notable_events'
    },
    {
      skip: !canReadConfig || !run?.custom_agent_config_id || !resultDocument
    }
  )
  const eventItems = (
    eventItemsQuery.isError ? [] : (eventItemsQuery.currentData?.results ?? [])
  ).filter((item) => item.run_id === runId)
  const [patchResultFeedback] = usePatchCustomAgentResultFeedbackMutation()

  if (runResult.isError) {
    return <Empty title="Couldn't load this custom-agent run" hint="Refresh to retry." />
  }
  if (runResult.isLoading || !run) {
    return <Empty title="Loading run…" />
  }

  if (
    run.trigger_kind === 'follow_up' &&
    run.parent_run_id &&
    run.config_snapshot?.workflow_version !== 'incident_v2'
  ) {
    return <Navigate to={runPageHref('custom', run.parent_run_id)} replace />
  }

  const resultSchema =
    (run.config_snapshot?.output_schema as Record<string, unknown> | undefined) ??
    config?.output_schema

  const presenter = hasResult ? pickPresenter(result, resultSchema) : 'generic'
  const headline = hasResult ? presenterHeadline(presenter, result) : undefined
  const invocations = run.invocations ?? []
  const configId = run.custom_agent_config_id || null
  const handleRerun = async () => {
    if (!configId) return
    try {
      setRerunError(null)
      const next = await dispatchRun({ configId }).unwrap()
      navigate(runPageHref('custom', next.id))
    } catch {
      setRerunError('Failed to start a new run.')
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <RunPageHeader
        title={headline ?? config?.name ?? 'Custom-agent run'}
        configId={canConfigure ? configId : null}
        canConfigure={canConfigure}
        configName={config?.name}
        runId={run.id}
        state={run.state}
        triggerKind={run.trigger_kind}
        completionStatus={run.completion_status ?? null}
        completionReason={run.completion_reason ?? null}
        completedAt={run.completed_at}
        createdAt={run.created_at}
        startedAt={run.started_at}
        finishedAt={run.finished_at}
        invocationCount={invocations.length}
        now={now}
        cancelling={cancelling}
        onCancel={
          canCancel
            ? async () => {
                await cancelRun(run.id).unwrap()
              }
            : undefined
        }
      />
      <div className="h-px bg-line" />

      {run.state === 'failed' ? (
        <>
          <RunFailure
            error={run.error}
            invocationCount={invocations.length}
            configId={canConfigure ? configId : null}
            onRerun={canDispatch && configId ? () => void handleRerun() : undefined}
            rerunning={rerunning}
          />
          {rerunError ? <p className="m-0 text-sm text-st-down">{rerunError}</p> : null}
        </>
      ) : null}
      {run.state === 'running' ? <RunProgress startedAt={run.started_at} now={now} /> : null}
      {run.state === 'pending_dispatch' ? <RunQueued /> : null}
      {run.state === 'cancelled' && !hasResult ? (
        <div className="rounded-lg border border-line bg-bg-elev px-4 py-3 text-sm text-ink-2">
          This run was cancelled.
        </div>
      ) : null}

      {run.config_snapshot?.workflow_version === 'incident_v2' && <LiveRunTasks runId={run.id} />}
      <div className="flex flex-col gap-4">
        {!canReadConfig && (
          <p className="text-13 text-ink-3">
            Report findings require access to this agent's results.
          </p>
        )}
        {canReadConfig && resultsQuery.isError && (
          <WorkError
            message="Couldn't load report findings."
            retry={() => void resultsQuery.refetch()}
          />
        )}
        {run.config_snapshot?.workflow_version === 'incident_v2' && (
          <h2 className="m-0 text-14 font-medium text-ink">
            Report findings · revision {selectedRevision}
          </h2>
        )}
        {currentRevision > 1 ? (
          <div className="flex justify-end">
            <FollowUpVersionPicker
              currentRevision={currentRevision}
              selectedRevision={selectedRevision}
              revisions={run.result_revisions}
              onSelect={(rev) => setViewRevision(rev === currentRevision ? null : rev)}
            />
          </div>
        ) : null}

        {viewingHistory ? (
          <HistoricalVersionBanner
            revision={selectedRevision}
            onBackToCurrent={() => setViewRevision(null)}
          />
        ) : null}

        {hasResult && result ? (
          presenter === 'rca' ? (
            <RcaPresenter result={result} />
          ) : presenter === 'pm' ? (
            <PmPresenter result={result} />
          ) : presenter === 'summary' ? (
            <SummaryPresenter
              result={result}
              eventItems={eventItems}
              onSetEventCorrect={
                viewingHistory ||
                !canReview ||
                eventItemsQuery.isFetching ||
                eventItemsQuery.isError
                  ? undefined
                  : async (eventId, correct, reason) => {
                      if (!correct) {
                        if (!reason?.trim()) return
                        await patchResultFeedback({
                          id: eventId,
                          ...(run.config_snapshot?.workflow_version === 'incident_v2'
                            ? {
                                problem_occurrence_id: eventItems.find(
                                  (item) => item.id === eventId
                                )?.problem_occurrence_id,
                                expected_revision: currentRevision
                              }
                            : {}),
                          approval_status: 'rejected',
                          feedback: reason.trim()
                        }).unwrap()
                        return
                      }
                      await patchResultFeedback({
                        id: eventId,
                        ...(run.config_snapshot?.workflow_version === 'incident_v2'
                          ? {
                              problem_occurrence_id: eventItems.find((item) => item.id === eventId)
                                ?.problem_occurrence_id,
                              expected_revision: currentRevision
                            }
                          : {}),
                        approval_status: 'approved'
                      }).unwrap()
                    }
              }
            />
          ) : (
            <RunSection label="Result">
              <SchemaAwareResultView value={result} schema={resultSchema} />
            </RunSection>
          )
        ) : null}

        {run.summary ? (
          <RunSection label="Summary">
            <div className="prose prose-sm dark:prose-invert max-w-none text-sm text-ink-2">
              <LazyChatMarkdown>{run.summary}</LazyChatMarkdown>
            </div>
          </RunSection>
        ) : null}

        <RunDetailDisclosure
          createdAt={run.created_at}
          startedAt={run.started_at}
          finishedAt={run.finished_at}
          invocations={invocations}
          now={now}
        />
      </div>
    </div>
  )
}

function LiveRunTasks({ runId }: { runId: string }) {
  const scope = useWorkScope()
  const query = useListProblemOccurrencesQuery({ ...scope, runId })
  if (query.isError)
    return <WorkError message="Couldn't load problems." retry={() => void query.refetch()} />
  return (
    <RunSection label="Problems">
      <DraftProblems scope={scope} runId={runId} />
      {!query.currentData ? (
        <p role="status">Loading problems…</p>
      ) : !query.currentData.length ? (
        <p className="text-13 text-ink-3">No published problems yet.</p>
      ) : (
        <ul className="m-0 flex list-none flex-col divide-y divide-line/70 overflow-hidden rounded-lg border border-line bg-card p-0">
          {query.currentData.map((occurrence) => (
            <ProblemSummaryRow
              key={occurrence.id}
              occurrence={occurrence}
              href={occurrenceHref(occurrence.id)}
            />
          ))}
        </ul>
      )}
    </RunSection>
  )
}
